import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import clsx from 'clsx';
import { Pin, PinOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Shortcut =
  | {
      key: string;
      ctrl?: boolean;
      meta?: boolean;
      alt?: boolean;
      shift?: boolean;
    }
  | string;

export type RailAction = {
  id: string;
  label: string;
  icon: React.ReactNode;
  shortcut?: Shortcut;
  onTrigger: () => void;
  active?: boolean;
  disabled?: boolean;
};

const normalizeShortcut = (sc?: Shortcut) => {
  if (!sc) return undefined;
  if (typeof sc === 'string')
    return { key: sc, ctrl: false, meta: false, alt: false, shift: false };
  return sc;
};

const matchShortcut = (
  e: KeyboardEvent,
  sc: ReturnType<typeof normalizeShortcut>,
) => {
  if (!sc) return false;
  const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
  const isTyping =
    ['input', 'textarea', 'select'].includes(tag) ||
    (e.target as HTMLElement)?.isContentEditable;
  if (isTyping) return false;

  const pressed = e.key.toLowerCase();
  const wanted = sc.key.toLowerCase();
  return (
    pressed === wanted &&
    !!sc.ctrl === (e.ctrlKey || false) &&
    !!sc.meta === (e.metaKey || false) &&
    !!sc.alt === (e.altKey || false) &&
    !!sc.shift === (e.shiftKey || false)
  );
};

const RightRailToggle = ({
  actions,
  className,
  defaultPinned = false,
}: {
  actions: RailAction[];
  className?: string;
  defaultPinned?: boolean;
}) => {
  const [pinned, setPinned] = useState(defaultPinned);
  const [hovering, setHovering] = useState(false);

  const isMobile = useIsMobile(1024);

  const expanded = isMobile ? pinned : pinned || hovering;

  const keyHandlers = useMemo(() => {
    return actions
      .filter((a) => a.shortcut && !a.disabled)
      .map((a) => ({
        id: a.id,
        onTrigger: a.onTrigger,
        sc: normalizeShortcut(a.shortcut),
      }));
  }, [actions]);

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      for (const h of keyHandlers) {
        if (matchShortcut(e, h.sc)) {
          e.preventDefault();
          h.onTrigger();
          break;
        }
      }
    },
    [keyHandlers],
  );

  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  return (
    <TooltipProvider>
      <aside
        className={clsx(
          'absolute right-0 top-0 h-full z-30 border bg-white backdrop-blur rounded-lg transition-[width] duration-200 ease-in',
          expanded ? 'w-64' : 'w-10',
          className,
        )}
        onMouseEnter={isMobile ? undefined : () => setHovering(true)}
        onMouseLeave={isMobile ? undefined : () => setHovering(false)}
        aria-label="Right rail"
      >
        <div className="flex items-center justify-between px-2 py-2 border-b">
          <span
            className={clsx(
              'text-xs font-medium text-muted-foreground transition-opacity',
              expanded ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
          >
            Panel
          </span>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setPinned((v) => !v)}
            aria-label={pinned ? 'Unpin rail' : 'Pin rail'}
            title={pinned ? 'Desanclar' : 'Anclar'}
          >
            {pinned ? (
              <PinOff className="h-4 w-4" />
            ) : (
              <Pin className="h-4 w-4" />
            )}
          </Button>
        </div>

        <div
          className={`flex flex-col ${
            expanded ? 'items-start' : 'items-center'
          } gap-1 p-2`}
        >
          {actions.map((a) => {
            const content = (
              <Button
                key={a.id}
                variant="ghost"
                size={expanded ? 'default' : 'icon'}
                className={clsx(
                  expanded ? 'justify-between w-full' : 'justify-center w-fit',
                  'h-9 hover:bg-white focus:bg-white',
                  a.disabled && 'opacity-50 pointer-events-none',
                )}
                onClick={a.onTrigger}
                aria-pressed={!!a.active}
              >
                <div className="flex items-center">
                  <span
                    className={clsx(
                      'shrink-0 p-1 rounded-lg transition-colors',
                      expanded && 'mr-2',
                      a.active &&
                        'bg-secondary text-secondary-foreground border',
                    )}
                  >
                    {a.icon}
                  </span>

                  {expanded && (
                    <span className="flex-1 text-sm truncate">{a.label}</span>
                  )}
                </div>

                {expanded && a.shortcut && (
                  <kbd className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    {typeof a.shortcut === 'string'
                      ? a.shortcut.toUpperCase()
                      : `${
                          a.shortcut.meta ? '⌘' : a.shortcut.ctrl ? 'Ctrl+' : ''
                        }${a.shortcut.alt ? 'Alt+' : ''}${
                          a.shortcut.shift ? 'Shift+' : ''
                        }${(a.shortcut.key || '').toUpperCase()}`}
                  </kbd>
                )}
              </Button>
            );

            return expanded ? (
              content
            ) : (
              <Tooltip delayDuration={100} key={a.id}>
                <TooltipTrigger asChild>{content}</TooltipTrigger>

                <TooltipContent side="left" align="center">
                  <div className="flex items-center gap-2">
                    <span>{a.label}</span>
                    {a.shortcut && (
                      <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {typeof a.shortcut === 'string'
                          ? a.shortcut.toUpperCase()
                          : `${
                              a.shortcut.meta
                                ? '⌘'
                                : a.shortcut.ctrl
                                ? 'Ctrl+'
                                : ''
                            }${a.shortcut.alt ? 'Alt+' : ''}${
                              a.shortcut.shift ? 'Shift+' : ''
                            }${(a.shortcut.key || '').toUpperCase()}`}
                      </kbd>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default RightRailToggle;
